/**
 * Ruteo de la aplicación.
 *
 * PÚBLICO (sin login): la raíz `/` y `/obs` muestran la vista de stream. Un espectador o
 * el streamer entran y ven la cartelera sin cuenta (§12).
 *
 * GESTIÓN (requiere sesión): el resto. Si no hay sesión, se redirige a `/login`.
 * Desde la vista pública se entra al panel con un botón que aparece al acercar el mouse
 * (o con Tab, para teclado).
 */
import { lazy, Suspense, type ReactNode } from 'react';
import { Navigate, Route, Routes, useLocation } from 'react-router-dom';
import { can, type Action, type Resource } from '@thepit/contracts';
import { useAuth } from './lib/auth.js';
import { useT } from './lib/i18n.js';
import { Layout } from './components/Layout.js';
import { Card, Skeleton } from './components/ui.js';
import { PanelEntry } from './components/PanelEntry.js';
import { Login } from './routes/Login.js';
import { StreamView } from './routes/StreamView.js';
import { PeleaActual } from './routes/PeleaActual.js';
import { MisApuestas } from './routes/MisApuestas.js';

const Mesa = lazy(() => import('./routes/Mesa.js').then((m) => ({ default: m.Mesa })));
const Caja = lazy(() => import('./routes/Caja.js').then((m) => ({ default: m.Caja })));
const Historial = lazy(() => import('./routes/Historial.js').then((m) => ({ default: m.Historial })));
const Ajustes = lazy(() => import('./routes/Ajustes.js').then((m) => ({ default: m.Ajustes })));
const Presets = lazy(() => import('./routes/Presets.js').then((m) => ({ default: m.Presets })));
const Usuarios = lazy(() => import('./routes/Usuarios.js').then((m) => ({ default: m.Usuarios })));
const Auditoria = lazy(() => import('./routes/Auditoria.js').then((m) => ({ default: m.Auditoria })));

function FullPageSkeleton(): JSX.Element {
  return (
    <div className="grid gap-3">
      <Skeleton height={120} />
      <Skeleton height={240} />
    </div>
  );
}

/** Guard de UI: si el rol no tiene el permiso, no se renderiza ni se descarga el chunk. */
function Guarded({
  resource,
  action,
  children,
}: {
  resource: Resource;
  action: Action;
  children: ReactNode;
}): JSX.Element {
  const { me, status } = useAuth();
  const t = useT();
  if (status === 'loading') return <FullPageSkeleton />;
  if (!me) return <Navigate to="/login" replace />;
  if (!can(me.user.role, resource, action)) {
    return (
      <Card title={t('error.forbidden')}>
        <p className="text-[var(--pit-text-secondary)]">{t('error.forbidden')}</p>
      </Card>
    );
  }
  return <>{children}</>;
}

function RequireSession({ children }: { children: ReactNode }): JSX.Element {
  const { me, status } = useAuth();
  const location = useLocation();
  if (status === 'loading') return <FullPageSkeleton />;
  if (!me) return <Navigate to="/login" state={{ from: location.pathname }} replace />;
  return <>{children}</>;
}

/** Envoltorio de las vistas públicas: fondo + botón de acceso al panel. */
function PublicShell({ children }: { children: ReactNode }): JSX.Element {
  return (
    <div className="pit-scanlines relative min-h-full bg-[var(--pit-bg-base)]">
      <PanelEntry />
      {/* Ancho completo: en OBS (/hud, /obs) el contenido tiene que llenar el cuadro,
          no quedar en un carril angosto con márgenes negros. */}
      <div className="w-full px-3 py-3 lg:px-5 lg:py-4">{children}</div>
    </div>
  );
}

export function App(): JSX.Element {
  const { me, status } = useAuth();

  return (
    <Suspense fallback={<FullPageSkeleton />}>
      <Routes>
        {/* Público: la cartelera, sin cuenta */}
        <Route
          path="/"
          element={
            <PublicShell>
              <StreamView />
            </PublicShell>
          }
        />
        <Route
          path="/obs"
          element={
            <PublicShell>
              <StreamView />
            </PublicShell>
          }
        />
        <Route
          path="/hud"
          element={
            <PublicShell>
              <StreamView forceMode="full" />
            </PublicShell>
          }
        />

        <Route path="/login" element={status === 'authenticated' ? <Navigate to="/pelea" replace /> : <Login />} />

        {/* Gestión: requiere sesión */}
        <Route
          element={
            <RequireSession>
              <Layout />
            </RequireSession>
          }
        >
          <Route path="/pelea" element={<PeleaActual />} />
          <Route path="/mis-apuestas" element={<MisApuestas />} />
          <Route
            path="/mesa"
            element={
              <Guarded resource="bet" action="create">
                <Mesa />
              </Guarded>
            }
          />
          <Route
            path="/caja"
            element={
              <Guarded resource="payout" action="pay">
                <Caja />
              </Guarded>
            }
          />
          <Route
            path="/historial"
            element={
              <Guarded resource="history" action="read">
                <Historial />
              </Guarded>
            }
          />
          <Route
            path="/ajustes"
            element={
              <Guarded resource="settings" action="update">
                <Ajustes />
              </Guarded>
            }
          />
          <Route
            path="/presets"
            element={
              <Guarded resource="preset" action="read">
                <Presets />
              </Guarded>
            }
          />
          <Route
            path="/usuarios"
            element={
              <Guarded resource="user" action="read">
                <Usuarios />
              </Guarded>
            }
          />
          <Route
            path="/auditoria"
            element={
              <Guarded resource="audit" action="read">
                <Auditoria />
              </Guarded>
            }
          />
        </Route>

        <Route path="*" element={<Navigate to={me ? '/pelea' : '/'} replace />} />
      </Routes>
    </Suspense>
  );
}
